import React from 'react';
import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import { RepositTheme } from '../../base/theme';
import { Caption, Header2 } from '../../components/Typography';
import { END_OF_TENANCY_GUIDE } from '../../constants/links';

export interface ClaimHeaderProps {
  isLatestAgentProposalFinal: boolean;
  hasTenantAutoAcceptedAnyCharges: boolean;
  hasTenantAcceptedAnyCharges: boolean;
  isTenantDeciding: boolean;
  address?: string;
  daysRemaining: number;
  organizationName: string;
  nothingToPay: boolean;
}

const Title = styled(Header2) <{ color: string }>`
  color: ${(props) => props.color};
  margin: 0px 0 1rem;
`;

const BoldText = styled.span`
  font-weight: bold;
  display: inline;
`;

const respondBeforeDeadlineCaption = () => (
  <Caption>
    Please take this opportunity to review the charges and respond. This will avoid the charges being automatically accepted, and
    your payment card being charged directly.
  </Caption>
);

const respondAfterDeadlineCaption = (hasTenantAutoAcceptedAnyCharges: boolean) => {
  if (hasTenantAutoAcceptedAnyCharges) {
    return (
      <Caption>
        The deadline to respond to the charges has passed and you have become automatically liable for the full amount.
      </Caption>
    );
  } else {
    return (
      <Caption>
        You must respond <BoldText>immediately</BoldText>, otherwise you will automatically become liable for the full amount.
      </Caption>
    );
  }
};

type ClaimUrgency = 'low' | 'medium' | 'high';

const calculateUrgency = (daysRemaining: number): ClaimUrgency => {
  if (daysRemaining < 0) {
    return 'high';
  } else if (daysRemaining < 4) {
    return 'medium';
  } else {
    return 'low';
  }
};

const titleColor = (urgency: ClaimUrgency) => {
  switch (urgency) {
    case 'low':
      return RepositTheme.colors.body;
    case 'medium':
      return RepositTheme.colors.warning;
    case 'high':
      return RepositTheme.colors.negative;
  }
};

export const canShowFinalProposalResponseInstruction = ({
  daysRemaining,
  isTenantDeciding,
  hasTenantAcceptedAnyCharges,
  hasTenantAutoAcceptedAnyCharges,
}: Pick<
  ClaimHeaderProps,
  'daysRemaining' | 'isTenantDeciding' | 'hasTenantAcceptedAnyCharges' | 'hasTenantAutoAcceptedAnyCharges'
>): boolean =>
  isTenantDeciding && daysRemaining >= 0 && !hasTenantAcceptedAnyCharges && !hasTenantAutoAcceptedAnyCharges;

const ClaimHeader: React.FC<ClaimHeaderProps> = ({
  hasTenantAutoAcceptedAnyCharges,
  hasTenantAcceptedAnyCharges,
  isTenantDeciding,
  isLatestAgentProposalFinal,
  address,
  daysRemaining,
  organizationName,
  nothingToPay,
}) => {
  const hasTimeRemaining = daysRemaining >= 0;
  const urgency: ClaimUrgency = calculateUrgency(daysRemaining);
  const pluraliseDays = daysRemaining === 1 ? 'day' : 'days';
  const showFinalProposalResponseInstruction = canShowFinalProposalResponseInstruction({
    daysRemaining,
    isTenantDeciding,
    hasTenantAcceptedAnyCharges,
    hasTenantAutoAcceptedAnyCharges,
  });

  return (
    <Row>
      {nothingToPay ? (
        <>
          <Col lg={8}>
            <Title color={RepositTheme.colors.body}>Payment plan in progress</Title>
            <Caption>
              Your co-tenant is currently on a payment plan to settle the end of tenancy charges. We'll notify you if there's
              anything further you need to do.
            </Caption>
          </Col>
        </>
      ) : (
        <>
          <Col lg={8}>
            <Title color={titleColor(urgency)}>
              {hasTimeRemaining
                ? daysRemaining === 0
                  ? `Your deadline to respond is midnight tonight`
                  : `You have ${daysRemaining} ${pluraliseDays} to respond`
                : `You must ${hasTenantAutoAcceptedAnyCharges ? 'pay' : 'respond'} immediately`}
            </Title>
            {isLatestAgentProposalFinal ? (
              <>
                <Caption>Your agent or landlord has reviewed your proposal and responded with their own.</Caption>
                {showFinalProposalResponseInstruction ? (
                  <Caption>
                    You may choose to accept this final settlement, or continue with a formal dispute process.
                  </Caption>
                ) : null}
              </>
            ) : (
              <Caption>
                End of tenancy charges have been raised on your Reposit by <BoldText>{organizationName}</BoldText>
                {address ? (
                  <span>
                    {' '}
                    for <BoldText>{address}</BoldText>
                  </span>
                ) : null}
                .
              </Caption>
            )}
            {hasTimeRemaining
              ? respondBeforeDeadlineCaption()
              : respondAfterDeadlineCaption(hasTenantAutoAcceptedAnyCharges)}
            <Caption>
              Want more information? Please check our{' '}
              <BoldText>
                <a href={END_OF_TENANCY_GUIDE} target="_blank" rel="noopener noreferrer">
                  End of Tenancy Guide
                </a>
              </BoldText>
              .
            </Caption>
          </Col>
        </>
      )}
    </Row>
  );
};

export default ClaimHeader;
